import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import { TaxSetupCard } from '../tax-setup/TaxSetupCard';
import { TaxSetupUsageCard } from '../tax-setup/TaxSetupUsageCard';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Calculator,
  Plus,
  Pencil,
  Loader2,
  RefreshCw,
  Sparkles,
  ArrowDownToLine,
  ArrowUpFromLine,
  Shield,
  Check,
  Info,
  Power,
  PowerOff,
  History,
  Calendar
} from 'lucide-react';

import { GLAccountSelect } from '../common/GLAccountSelector';
import { BusinessTaxProfileWizard } from '../tax/BusinessTaxProfileWizard';

const TaxSettingsTab = ({ business, refreshBusiness }) => {
  const [taxRules, setTaxRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [taxSummary, setTaxSummary] = useState(null);
  
  // iter971k-C P1 - Master tax-tracking toggle (business.enable_tax_management)
  const [masterEnabled, setMasterEnabled] = useState(
    business?.enable_tax_management !== false
  );
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [savingMasterToggle, setSavingMasterToggle] = useState(false);

  // iter971k-D - Business Tax Profile wizard state
  const [showTaxProfileWizard, setShowTaxProfileWizard] = useState(false);
  
  // Dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [togglingRuleId, setTogglingRuleId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    rate: '',
    tax_type: 'output',
    description: '',
    gl_account_code: '2200',
    applicable_to: [],
    is_default: false,
    is_active: true,
    effective_start_date: '',
    wht_direction: null  // Iter 884: 'payable' | 'receivable' | null
  });

  // Iter 884 - Default GL codes per tax-type / WHT direction.
  // These are the typical Sage/QBO defaults; the user can still override via the GL dropdown.
  const defaultGLForType = (taxType, whtDirection) => {
    if (taxType === 'output') return '2200';                     // Output VAT / Sales Tax Payable
    if (taxType === 'input') return '1400';                      // Input VAT Receivable
    if (taxType === 'withholding' && whtDirection === 'receivable') return '1420';  // WHT Receivable
    if (taxType === 'withholding') return '2320';                // WHT Payable (default)
    return '';
  };

  // Iter 884 - Which COA account types should the GL dropdown show for the chosen tax type?
  const accountTypesForTaxType = (taxType, whtDirection) => {
    if (taxType === 'output') return ['liability'];
    if (taxType === 'input') return ['asset'];
    if (taxType === 'withholding' && whtDirection === 'receivable') return ['asset'];
    if (taxType === 'withholding') return ['liability'];
    return ['asset', 'liability'];
  };

  // Iter 884 - UI dropdown packs WHT direction into the tax_type for convenience,
  // but persists separately on the server. These helpers translate between the two.
  const toUiTaxType = (rule) => {
    if (rule?.tax_type === 'withholding') {
      return rule?.wht_direction === 'receivable' ? 'withholding_receivable' : 'withholding_payable';
    }
    return rule?.tax_type || 'output';
  };

  const fromUiTaxType = (uiValue) => {
    if (uiValue === 'withholding_payable') return { tax_type: 'withholding', wht_direction: 'payable' };
    if (uiValue === 'withholding_receivable') return { tax_type: 'withholding', wht_direction: 'receivable' };
    return { tax_type: uiValue, wht_direction: null };
  };

  // Iter 884 - When the user changes the tax-type dropdown, snap the GL to the type-appropriate default
  const handleTaxTypeChange = (uiValue) => {
    const { tax_type, wht_direction } = fromUiTaxType(uiValue);
    setFormData((prev) => ({
      ...prev,
      tax_type,
      wht_direction,
      gl_account_code: defaultGLForType(tax_type, wht_direction)
    }));
  };

  const categoryOptions = [
    { value: 'goods', label: 'Goods' },
    { value: 'services', label: 'Services' },
    { value: 'professional_fees', label: 'Professional Fees' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'rent', label: 'Rent' },
    { value: 'legal_fees', label: 'Legal Fees' },
    { value: 'management_fees', label: 'Management Fees' },
    { value: 'supplies', label: 'Supplies' },
    { value: 'purchases', label: 'Purchases' },
    { value: 'expenses', label: 'General Expenses' },
  ];

  const fetchTaxRules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tax-management/rules`);
      setTaxRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching tax rules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTaxSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tax-management/summary`);
      setTaxSummary(response.data);
    } catch (error) {
      console.error('Error fetching tax summary:', error);
    }
  }, []);

  // iter971k-C P1 - removed the "Tax Activation" per-type toggle
  // effect + handlers; the master `Tax Tracking` card now covers the
  // silence-all-suggestions intent. Individual rule enable/disable
  // lives on the per-rule row in the Tax Rules table below.

  useEffect(() => {
    fetchTaxRules();
    fetchTaxSummary();
  }, [fetchTaxRules, fetchTaxSummary]);

  // Keep master toggle in sync when business prop changes (e.g. onboarding wraps up)
  useEffect(() => {
    if (business && typeof business.enable_tax_management !== 'undefined') {
      setMasterEnabled(business.enable_tax_management !== false);
    }
  }, [business?.enable_tax_management]);

  // iter971k-B - Master tax-tracking toggle
  // When OFF: platform will NOT suggest VAT/WHT on invoices, bills, or bank feed.
  // Payroll statutory tax (PAYE/NI/pension/NHF/etc.) is NEVER affected - those are
  // legally mandatory and continue to be applied.
  const applyMasterToggle = async (nextEnabled) => {
    setSavingMasterToggle(true);
    try {
      await axios.patch(`${API}/business/tax-settings`, null, {
        params: { enable_tax_management: nextEnabled },
      });
      setMasterEnabled(nextEnabled);
      if (nextEnabled) {
        toast.success('Tax tracking turned on: we will suggest VAT/WHT across your transactions.');
        // iter971k-D - First-time enable: open the layman questionnaire so
        // the AI has proper context before suggesting taxes. Skip if the
        // user has already completed the profile.
        if (!business?.tax_profile_updated_at) {
          setShowTaxProfileWizard(true);
        }
      } else {
        toast.success('Tax tracking turned off: no VAT/WHT will be suggested on new transactions.');
      }
      if (refreshBusiness) await refreshBusiness();
    } catch (error) {
      toast.error('Could not update tax tracking setting');
    } finally {
      setSavingMasterToggle(false);
      setShowDisableConfirm(false);
    }
  };

  const handleMasterToggle = (nextEnabled) => {
    if (nextEnabled === false) {
      // Explicit confirmation only when turning OFF - enabling is safe.
      setShowDisableConfirm(true);
    } else {
      applyMasterToggle(true);
    }
  };

  const handleInitializeTaxes = async () => {
    setInitializing(true);
    try {
      const response = await axios.post(`${API}/tax-management/initialize`);
      if (response.data.initialized) {
        toast.success(`${response.data.count} tax rules created for your country`, {
          description: `Source: ${response.data.source === 'ai' ? 'AI-generated based on current regulations' : 'Standard template'}`
        });
        fetchTaxRules();
        fetchTaxSummary();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to initialize tax rules');
    } finally {
      setInitializing(false);
    }
  };

  const handleRefreshTaxes = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/tax-management/refresh`);
      if (response.data.refreshed) {
        toast.success('Tax rules refreshed with latest regulations', {
          description: `${response.data.new_rules_added} new rules added, ${response.data.user_rules_preserved} custom rules preserved`
        });
        fetchTaxRules();
        fetchTaxSummary();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to refresh tax rules');
    } finally {
      setRefreshing(false);
    }
  };

  const openAddDialog = () => {
    setFormData({
      name: '',
      code: '',
      rate: '',
      tax_type: 'output',
      description: '',
      gl_account_code: '2200',
      applicable_to: [],
      is_default: false,
      is_active: true,
      effective_start_date: new Date().toISOString().split('T')[0],
      wht_direction: null
    });
    setShowAddDialog(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      code: rule.code,
      rate: rule.rate.toString(),
      tax_type: rule.tax_type,
      description: rule.description || '',
      gl_account_code: rule.gl_account_code,
      applicable_to: rule.applicable_to || [],
      is_default: rule.is_default,
      is_active: rule.is_active,
      effective_start_date: rule.effective_start_date || '',
      wht_direction: rule.tax_type === 'withholding' ? (rule.wht_direction || 'payable') : null
    });
    setShowEditDialog(true);
  };

  const openHistoryDialog = (rule) => {
    setEditingRule(rule);
    setShowHistoryDialog(true);
  };

  const handleSaveRule = async (isEdit = false) => {
    if (!formData.name || !formData.code || !formData.rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingRule(true);
    try {
      const payload = {
        ...formData,
        rate: parseFloat(formData.rate)
      };

      if (isEdit && editingRule) {
        const response = await axios.put(`${API}/tax-management/rules/${editingRule.id}`, payload);
        if (response.data.rate_history_updated) {
          toast.success('Tax rule updated with new rate', {
            description: `New rate of ${formData.rate}% effective from ${formData.effective_start_date || 'today'}`
          });
        } else {
          toast.success('Tax rule updated');
        }
        setShowEditDialog(false);
      } else {
        await axios.post(`${API}/tax-management/rules`, payload);
        toast.success('Tax rule created');
        setShowAddDialog(false);
      }
      
      fetchTaxRules();
      fetchTaxSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tax rule');
    } finally {
      setSavingRule(false);
    }
  };

  const handleToggleActive = async (rule) => {
    setTogglingRuleId(rule.id);
    try {
      if (rule.is_active) {
        // Deactivate
        await axios.delete(`${API}/tax-management/rules/${rule.id}`);
        toast.success('Tax rule deactivated', {
          description: 'It will no longer appear for new transactions'
        });
      } else {
        // Reactivate
        await axios.post(`${API}/tax-management/rules/${rule.id}/reactivate`);
        toast.success('Tax rule reactivated');
      }
      fetchTaxRules();
      fetchTaxSummary();
    } catch (error) {
      toast.error(`Failed to ${rule.is_active ? 'deactivate' : 'reactivate'} tax rule`);
    } finally {
      setTogglingRuleId(null);
    }
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      applicable_to: prev.applicable_to.includes(category)
        ? prev.applicable_to.filter(c => c !== category)
        : [...prev.applicable_to, category]
    }));
  };

  const getTaxTypeIcon = (type) => {
    switch (type) {
      case 'output':
        return <ArrowUpFromLine className="w-4 h-4 text-blue-500" />;
      case 'input':
        return <ArrowDownToLine className="w-4 h-4 text-green-500" />;
      case 'withholding':
        return <Shield className="w-4 h-4 text-amber-500" />;
      default:
        return <Calculator className="w-4 h-4" />;
    }
  };

  const getTaxTypeBadge = (type, whtDirection) => {
    const styles = {
      output: 'bg-blue-100 text-blue-700 border-blue-200',
      input: 'bg-green-100 text-green-700 border-green-200',
      withholding_payable: 'bg-amber-100 text-amber-700 border-amber-200',
      withholding_receivable: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    const labels = {
      output: 'Sales Tax',
      input: 'Input (Recoverable)',
      withholding_payable: 'WHT Payable',
      withholding_receivable: 'WHT Receivable'
    };
    // Iter 884 - collapse {tax_type, wht_direction} to a single key for styling
    const key = type === 'withholding'
      ? (whtDirection === 'receivable' ? 'withholding_receivable' : 'withholding_payable')
      : type;
    return (
      <Badge variant="outline" className={styles[key] || 'bg-gray-100'}>
        {labels[key] || type}
      </Badge>
    );
  };

  const renderRuleDialog = (isEdit = false) => (
    <Dialog open={isEdit ? showEditDialog : showAddDialog} onOpenChange={isEdit ? setShowEditDialog : setShowAddDialog}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#283F8B]" />
            {isEdit ? 'Edit Tax Rule' : 'Add Tax Rule'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modify this tax rule. Rate changes will apply to future transactions from the effective date.' : 'Create a new tax rule for your business.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Tax Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Value Added Tax"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Tax Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., VAT"
                disabled={isEdit}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rate">Rate (%) *</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                placeholder="e.g., 7.5"
              />
              {isEdit && editingRule && parseFloat(formData.rate) !== editingRule.rate && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <History className="w-3 h-3" />
                  Rate change from {editingRule.rate}% → {formData.rate}%
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax_type">Tax Type *</Label>
              <Select 
                value={toUiTaxType(formData)} 
                onValueChange={handleTaxTypeChange}
              >
                <SelectTrigger id="tax_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="output">
                    <span className="flex items-center gap-2">
                      <ArrowUpFromLine className="w-4 h-4 text-blue-500" />
                      Output (Sales Tax)
                    </span>
                  </SelectItem>
                  <SelectItem value="input">
                    <span className="flex items-center gap-2">
                      <ArrowDownToLine className="w-4 h-4 text-green-500" />
                      Input (Recoverable)
                    </span>
                  </SelectItem>
                  <SelectItem value="withholding_payable">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-amber-500" />
                      WHT Payable (we withhold from suppliers)
                    </span>
                  </SelectItem>
                  <SelectItem value="withholding_receivable">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-500" />
                      WHT Receivable (customers withhold from us)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.tax_type === 'withholding' && formData.wht_direction === 'receivable'
                  ? 'You are the supplier. Customer withholds and remits. Asset (claimable against income tax).'
                  : formData.tax_type === 'withholding'
                  ? 'You are the buyer. You withhold from supplier payment and remit to tax authority. Liability.'
                  : formData.tax_type === 'input'
                  ? 'VAT paid on purchases: claimable from tax authority. Asset.'
                  : 'VAT charged on sales. Owed to tax authority. Liability.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gl_account_code">GL Account</Label>
              <GLAccountSelect
                value={formData.gl_account_code}
                onChange={(code) => setFormData({ ...formData, gl_account_code: code })}
                accountTypes={accountTypesForTaxType(formData.tax_type, formData.wht_direction)}
                placeholder="Select GL account"
                data-testid="tax-rule-gl-select"
              />
              <p className="text-xs text-muted-foreground">
                {formData.tax_type === 'output' && 'Defaults to a Liability account (Output VAT / Sales Tax Payable, e.g. 2200).'}
                {formData.tax_type === 'input' && 'Defaults to an Asset account (Input VAT Receivable, e.g. 1400).'}
                {formData.tax_type === 'withholding' && formData.wht_direction === 'receivable' && 'Defaults to an Asset account (WHT Receivable, e.g. 1420). Claimable against income tax.'}
                {formData.tax_type === 'withholding' && formData.wht_direction !== 'receivable' && 'Defaults to a Liability account (WHT Payable, e.g. 2320). Owed to tax authority.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="effective_start_date" className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Effective Date
              </Label>
              <Input
                id="effective_start_date"
                type="date"
                value={formData.effective_start_date}
                onChange={(e) => setFormData({ ...formData, effective_start_date: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Rate applies to transactions from this date
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of when this tax applies"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Applicable Categories</Label>
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => toggleCategory(cat.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    formData.applicable_to.includes(cat.value)
                      ? 'bg-[#108981] text-white border-[#108981]'
                      : 'bg-muted hover:bg-muted/80 border-border'
                  }`}
                >
                  {formData.applicable_to.includes(cat.value) && <Check className="w-3 h-3 inline mr-1" />}
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium text-sm">Default Tax</p>
              <p className="text-xs text-muted-foreground">Auto-suggest this tax for applicable transactions</p>
            </div>
            <Switch
              checked={formData.is_default}
              onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => isEdit ? setShowEditDialog(false) : setShowAddDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSaveRule(isEdit)}
            disabled={savingRule}
            className="bg-[#283F8B] hover:bg-[#283F8B]/90"
          >
            {savingRule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Create Tax Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderHistoryDialog = () => (
    <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#283F8B]" />
            Rate History: {editingRule?.name}
          </DialogTitle>
          <DialogDescription>
            Historical rate changes for this tax rule
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {editingRule?.rate_history && editingRule.rate_history.length > 0 ? (
            <div className="space-y-3">
              {[...editingRule.rate_history].reverse().map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">{entry.rate}%</p>
                    <p className="text-xs text-muted-foreground">
                      Effective: {entry.effective_date}
                    </p>
                  </div>
                  {entry.previous_rate !== undefined && (
                    <Badge variant="outline" className="text-xs">
                      from {entry.previous_rate}%
                    </Badge>
                  )}
                  {idx === 0 && (
                    <Badge className="bg-[#108981]">Current</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              No rate history available
            </p>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-settings-tab">
      <TaxSetupCard />
      <TaxSetupUsageCard />
      {/* iter971k-B - Master Tax Tracking Toggle */}
      <Card className="border-l-4 border-l-[#283F8B]" data-testid="tax-master-toggle-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#283F8B]" />
            Tax Tracking
          </CardTitle>
          <CardDescription>
            Suggests VAT and Withholding Tax on invoices, bills and bank transactions. Payroll taxes are tracked separately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`flex items-center justify-between p-4 rounded-lg border ${
              masterEnabled ? 'bg-[#108981]/5 border-[#108981]/30' : 'bg-muted/30 border-border'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-full ${
                  masterEnabled ? 'bg-[#108981]/15' : 'bg-muted'
                }`}
              >
                <Power
                  className={`w-4 h-4 ${
                    masterEnabled ? 'text-[#108981]' : 'text-muted-foreground'
                  }`}
                />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {masterEnabled
                    ? 'Tax tracking is on'
                    : 'Tax tracking is off'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {masterEnabled
                    ? "We'll suggest VAT/WHT everywhere you record income or expenses."
                    : 'No VAT/WHT suggestions on new invoices, bills, or bank transactions.'}
                </p>
              </div>
            </div>
            <Switch
              checked={masterEnabled}
              onCheckedChange={handleMasterToggle}
              disabled={savingMasterToggle}
              data-testid="tax-master-toggle"
            />
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#283F8B]" />
            <p data-testid="tax-master-toggle-privacy-note">
              For your records only. Nothing is filed or shared — you file from the Tax Compliance page.
            </p>
          </div>

          {/* iter971k-D - Business Tax Profile launcher (only when tracking is ON) */}
          {masterEnabled && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-lg border border-dashed border-[#283F8B]/40 bg-[#283F8B]/5">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 text-[#283F8B]" />
                <div>
                  <p className="text-sm font-medium">
                    {business?.tax_profile_updated_at
                      ? 'Your business tax profile is set'
                      : 'Tell us a bit about your business'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {business?.tax_profile_updated_at
                      ? 'We use these answers to suggest the right taxes. You can update them anytime.'
                      : 'A few quick questions so we know which taxes actually apply to you. Takes under a minute.'}
                  </p>
                </div>
              </div>
              <Button
                variant={business?.tax_profile_updated_at ? 'outline' : 'default'}
                size="sm"
                onClick={() => setShowTaxProfileWizard(true)}
                data-testid="open-tax-profile-wizard-btn"
              >
                {business?.tax_profile_updated_at ? 'Edit answers' : 'Start'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* iter971k-D - Layman Business Tax Profile wizard */}
      <BusinessTaxProfileWizard
        open={showTaxProfileWizard}
        country={business?.country}
        onClose={() => setShowTaxProfileWizard(false)}
        onCompleted={async () => {
          if (refreshBusiness) await refreshBusiness();
        }}
      />

      {/* Confirm dialog when turning master OFF */}
      <Dialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <DialogContent data-testid="tax-master-disable-confirm">
          <DialogHeader>
            <DialogTitle>Turn off tax tracking?</DialogTitle>
            <DialogDescription className="pt-2">
              No more VAT / WHT suggestions on new records. Existing records keep their tax as is.
              <br /><br />
              You can turn this back on any time: including for past transactions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDisableConfirm(false)}
              data-testid="tax-master-disable-cancel"
            >
              Keep it on
            </Button>
            <Button
              onClick={() => applyMasterToggle(false)}
              disabled={savingMasterToggle}
              data-testid="tax-master-disable-confirm-btn"
            >
              {savingMasterToggle ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Turn off tax tracking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter971k-C P1 - Removed the duplicate "Tax Tracking Activation"
          card. The master "Tax Tracking" card above already covers the
          intent (silences ALL VAT/WHT suggestions when off), and the
          per-rule toggles below (Tax Rules table) let users enable/
          disable individual VAT / WHT rules with more precision. Keeping
          both created a confusing overlap. */}

      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#283F8B]" />
                Tax Rules
                {taxSummary && (
                  <Badge variant="secondary" className="ml-2">
                    {taxSummary.country_name}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Rates used on invoices and bills. Kept current by AI.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {taxRules.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshTaxes}
                  disabled={refreshing}
                  className="gap-2"
                >
                  {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh from AI
                </Button>
              )}
              <Button
                onClick={openAddDialog}
                size="sm"
                className="bg-[#108981] hover:bg-[#108981]/90 gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Tax Rule
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {taxRules.length === 0 && (
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#283F8B]/10 to-[#108981]/10 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-[#283F8B]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Initialize Tax Rules</h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                AI will generate country-specific tax rules based on current regulations for {taxSummary?.country_name || 'your country'}.
                You can customize them afterwards.
              </p>
              <Button
                onClick={handleInitializeTaxes}
                disabled={initializing}
                className="bg-[#283F8B] hover:bg-[#283F8B]/90 gap-2"
              >
                {initializing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Tax Rules with AI
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Tax Summary Cards */}
      {taxSummary && taxRules.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <ArrowUpFromLine className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Output Taxes (Sales)</p>
                  <p className="text-2xl font-bold text-blue-700">{taxSummary.output_taxes.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <ArrowDownToLine className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Input Taxes (Recoverable)</p>
                  <p className="text-2xl font-bold text-green-700">{taxSummary.input_taxes.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Withholding Taxes</p>
                  <p className="text-2xl font-bold text-amber-700">{taxSummary.withholding_taxes.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tax Rules List */}
      {taxRules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All Tax Rules</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {taxRules.map((rule) => (
                <div 
                  key={rule.id}
                  className={`p-4 flex items-center justify-between hover:bg-muted/50 transition-colors ${!rule.is_active ? 'opacity-60 bg-muted/30' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      {getTaxTypeIcon(rule.tax_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{rule.name}</p>
                        <Badge variant="outline" className="text-xs">{rule.code}</Badge>
                        {rule.ai_generated && (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <Sparkles className="w-3 h-3" />
                            AI
                          </Badge>
                        )}
                        {rule.is_default && (
                          <Badge className="bg-[#108981] text-xs">Default</Badge>
                        )}
                        {!rule.is_active && (
                          <Badge variant="destructive" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {getTaxTypeBadge(rule.tax_type, rule.wht_direction)}
                        <span className="text-sm text-muted-foreground">
                          GL: {rule.gl_account_code}
                        </span>
                        {rule.effective_start_date && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {rule.effective_start_date}
                          </span>
                        )}
                      </div>
                      {rule.description && (
                        <p className="text-xs text-muted-foreground mt-1">{rule.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#283F8B]">{rule.rate}%</p>
                      {rule.applicable_to?.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {rule.applicable_to.slice(0, 2).join(', ')}
                          {rule.applicable_to.length > 2 && ` +${rule.applicable_to.length - 2}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {rule.rate_history && rule.rate_history.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openHistoryDialog(rule)}
                          className="h-8 w-8"
                          title="View rate history"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(rule)}
                        className="h-8 w-8"
                        title="Edit tax rule"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleActive(rule)}
                        disabled={togglingRuleId === rule.id}
                        className={`h-8 w-8 ${rule.is_active ? 'text-amber-600 hover:text-amber-700' : 'text-green-600 hover:text-green-700'}`}
                        title={rule.is_active ? 'Deactivate tax rule' : 'Reactivate tax rule'}
                      >
                        {togglingRuleId === rule.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : rule.is_active ? (
                          <PowerOff className="w-4 h-4" />
                        ) : (
                          <Power className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      {taxRules.length > 0 && (
        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How Tax Rules Work</p>
                <ul className="space-y-1 text-blue-700">
                  <li>
                    <strong>Output taxes</strong> (like VAT) are added to your invoices and collected from customers
                  </li>
                  <li>
                    <strong>Input taxes</strong> are taxes you pay on purchases that may be recoverable
                  </li>
                  <li>
                    <strong>Withholding taxes</strong> are deducted from vendor payments and remitted to tax authorities
                  </li>
                  <li>
                    Taxes marked as <strong>Default</strong> will be automatically suggested for applicable transactions
                  </li>
                  <li>
                    <strong>Rate changes</strong> take effect from the effective date and are tracked in history
                  </li>
                  <li>
                    Taxes can be deactivated, not deleted (they may be on past records)
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {renderRuleDialog(false)}
      {renderRuleDialog(true)}
      {renderHistoryDialog()}
    </div>
  );
};

export default TaxSettingsTab;
